import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  FileCheck,
  FileText,
  UserCheck,
  Eye,
  Download,
  Trash2,
  Upload,
  Loader2,
  Plus,
  Calendar,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "react-toastify";
import { format } from "date-fns";
import apiService from "../../services/apiService";
import { getLocalStorageItem } from "../../utils";
import { fetchManageInvoiceDocUrl, MANAGE_INVOICE_DOC_URL_TTL_MS } from "../../lib/manageInvoiceDoc";

interface ApiResponse<T> {
  data: T;
  data_two: T;
  message?: string;
  status?: number;
  documents: T;
}

interface DocType {
  id: number | string;
  document_type_id: number | string;
  value_slug: string;
  value_name: string;
}

interface UploadedDoc {
  id: number | string;
  document_id?: number | string;
  document_name?: string;
  file_name?: string;
  name?: string;
  url?: string;
  file_url?: string;
  fileUrl?: string;
  document_url?: string;
  documentUrl?: string;
  s3_url?: string;
  file_path?: string;
  uploadedAt?: string;
  uploaded_at?: string;
  created_at?: string;
  file_size?: number;
  fileSize?: number;
  size?: number;
  document_type_id?: number | string;
  document_type_value_id?: number | string;
  documentTypeValueId?: number | string;
  value_slug?: string;
  admin_name?: string;
}

interface UploadedTypeRecord extends UploadedDoc {
  document_type_value_id: number | string;
}

const DOCUMENT_TYPE_ID = 4;

const OTHER_TYPE_FALLBACK: DocType = {
  id: "other",
  document_type_id: DOCUMENT_TYPE_ID,
  value_slug: "other",
  value_name: "Other",
};

// Detect "Other" by slug/name only — never by numeric id.
// document_type_id is always 4 for RS docs, and value id 4 is
// "Signed Notice of Assignment", not "Other".
const isOtherType = (type: Pick<DocType, "id" | "value_slug" | "value_name">) => {
  const slug = String(type.value_slug || "")
    .toLowerCase()
    .replace(/[-\s]/g, "_")
    .replace(/_+/g, "_");
  const name = String(type.value_name || "").toLowerCase().trim();
  return slug === "other" || name === "other";
};

const formatDocDate = (dateString?: string) => {
  if (!dateString) return "";
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return dateString;
  return format(parsed, "MMM d, yyyy");
};

const getDocName = (doc?: UploadedDoc) =>
  doc?.name || doc?.document_name || "Document";

const getDocUrl = (doc?: UploadedDoc) =>
  doc?.url ||
  doc?.file_url ||
  doc?.fileUrl ||
  doc?.s3_url ||
  doc?.document_url ||
  doc?.documentUrl ||
  doc?.file_path ||
  "";

const getDocDate = (doc?: UploadedDoc) =>
  doc?.uploadedAt || doc?.uploaded_at || doc?.created_at || "";

const getDocUploader = (doc?: UploadedDoc) => doc?.admin_name || "";

const getManageDocId = (doc?: UploadedDoc) => doc?.id ?? doc?.document_id;

const stampDocUrlFetchTimes = (
  ref: { current: Record<string, number> },
  docs: UploadedDoc[],
) => {
  const now = Date.now();
  for (const doc of docs) {
    if (doc.id != null) ref.current[String(doc.id)] = now;
    if (doc.document_id != null) ref.current[String(doc.document_id)] = now;
  }
};

const mergeUploadedDocs = (...docs: Array<UploadedDoc | undefined>): UploadedDoc | undefined => {
  const present = docs.filter((doc): doc is UploadedDoc => Boolean(doc));
  if (!present.length) return undefined;
  const merged = present.reduce<UploadedDoc>((acc, doc) => ({ ...acc, ...doc }), {} as UploadedDoc);
  return {
    ...merged,
    url: present.map(getDocUrl).find(Boolean) || merged.url,
    document_name:
      present.map(getDocName).find((name) => name && name !== "Document") || merged.document_name,
    uploadedAt: present.map(getDocDate).find(Boolean) || merged.uploadedAt,
    admin_name: present.map(getDocUploader).find(Boolean) || merged.admin_name,
  };
};

const getTypeValueId = (doc: UploadedDoc) =>
  doc.document_type_value_id ?? doc.documentTypeValueId;

const matchesType = (doc: UploadedDoc, type: DocType) => {
  const docTypeId = getTypeValueId(doc);
  if (docTypeId != null && (docTypeId == type.id || String(docTypeId) === String(type.id))) {
    return true;
  }
  if (doc.value_slug && type.value_slug) {
    return String(doc.value_slug).toLowerCase() === String(type.value_slug).toLowerCase();
  }
  return false;
};

interface ScheduleDocumentsTabProps {
  rentalScheduleId: string | number;
  customerId: string;
  onViewSchedule: () => void;
  onViewLeasingDocs: () => void;
  onViewSignatories: () => void;
}

export default function ScheduleDocumentsTab({
  rentalScheduleId,
  customerId,
  onViewSchedule,
  onViewLeasingDocs,
  onViewSignatories,
}: ScheduleDocumentsTabProps) {
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [uploadedTypeRecords, setUploadedTypeRecords] = useState<UploadedTypeRecord[]>([]);
  const [uploadedTypeIds, setUploadedTypeIds] = useState<Array<number | string>>([]);
  const [documents, setDocuments] = useState<UploadedDoc[]>([]);
  const [uploadingTypeId, setUploadingTypeId] = useState<string>("");
  const [uploadingOther, setUploadingOther] = useState(false);
  const [customFileName, setCustomFileName] = useState("");
  const [pendingStatutoryType, setPendingStatutoryType] = useState<DocType | null>(null);
  const [viewDocOpen, setViewDocOpen] = useState(false);
  const [popupName, setPopupName] = useState("");
  const [popupUrl, setPopupUrl] = useState("");
  const [popupDate, setPopupDate] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<number | string>();
  const [resolvingDocId, setResolvingDocId] = useState<string | null>(null);
  const statutoryFileRef = useRef<HTMLInputElement>(null);
  const otherFileRef = useRef<HTMLInputElement>(null);
  const docUrlFetchedAtRef = useRef<Record<string, number>>({});

  const fetchDocTypes = useCallback(async () => {
    if (!rentalScheduleId || !customerId) return;
    try {
      const document_type_id = DOCUMENT_TYPE_ID;
      const response = await apiService.get<ApiResponse<any[]>>(
        `/rental-schedule/get_Rs_Doc_Types`,
        {
          params: {
            document_type_id,
            rental_schedule_id: rentalScheduleId,
            customer_id: customerId,
          },
        }
      );

      const response1 = Array.isArray(response.data) ? response.data : [];
      const response2 = Array.isArray(response.data_two) ? response.data_two : [];
      const idsFromResponse2 = response2.map((item) => item.document_type_value_id);

      setDocTypes(response1);
      setUploadedTypeRecords(response2);
      setUploadedTypeIds(idsFromResponse2);
      stampDocUrlFetchTimes(docUrlFetchedAtRef, response2);
    } catch (error) {
      console.error("Failed to fetch document types:", error);
      toast.error("Failed to load document types");
    }
  }, [rentalScheduleId, customerId]);

  const fetchDocuments = useCallback(async () => {
    if (!rentalScheduleId || !customerId) return;
    try {
      const document_type_id = DOCUMENT_TYPE_ID;
      const [response] = await Promise.all([
        apiService.get<ApiResponse<any>>(
          `/rental-schedule/getRsDocs/${customerId}`,
          {
            params: {
              document_type_id,
              rental_schedule_id: rentalScheduleId,
            },
          }
        ),
      ]);
      const nestedDocs = (response as any)?.data?.documents;
      const docs = Array.isArray(response?.documents)
        ? response.documents
        : Array.isArray(nestedDocs)
          ? nestedDocs
          : Array.isArray((response as any)?.data)
            ? (response as any).data
            : [];
      setDocuments(docs);
      stampDocUrlFetchTimes(docUrlFetchedAtRef, docs);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      toast.error("Failed to load documents");
    }
  }, [rentalScheduleId, customerId]);

  useEffect(() => {
    fetchDocTypes();
    fetchDocuments();
  }, [fetchDocTypes, fetchDocuments]);

  const isTypeUploaded = (type: DocType) =>
    uploadedTypeIds.some((id) => id == type.id || String(id) === String(type.id));

  const statutoryTypes = useMemo(
    () => docTypes.filter((type) => !isOtherType(type)),
    [docTypes]
  );

  const otherType = useMemo(
    () => docTypes.find((type) => isOtherType(type)) ?? OTHER_TYPE_FALLBACK,
    [docTypes]
  );

  const getDocForType = (type: DocType) => {
    const fromTwo = uploadedTypeRecords.find(
      (item) =>
        item.document_type_value_id == type.id ||
        String(item.document_type_value_id) === String(type.id)
    );
    const fromDocs =
      documents.find((doc) => matchesType(doc, type)) ||
      documents.find(
        (doc) =>
          fromTwo &&
          (String(doc.id) === String(fromTwo.id) ||
            String(doc.id) === String(fromTwo.document_id) ||
            (doc.document_name &&
              fromTwo.document_name &&
              doc.document_name === fromTwo.document_name))
      );
    return mergeUploadedDocs(fromTwo, fromDocs);
  };

  const resolveFileDoc = (doc: UploadedDoc) => {
    const fromDocs = documents.find((item) => {
      if (String(item.id) === String(doc.id) || String(item.id) === String(doc.document_id)) {
        return true;
      }
      return Boolean(
        doc.document_name && item.document_name && item.document_name === doc.document_name
      );
    });
    return mergeUploadedDocs(doc, fromDocs) || doc;
  };

  const otherDocuments = useMemo(() => {
    const statutoryTypeIds = new Set(statutoryTypes.map((type) => String(type.id)));
    const statutoryFileIds = new Set<string>();

    statutoryTypes.forEach((type) => {
      documents.forEach((doc) => {
        if (matchesType(doc, type) && doc.id != null) {
          statutoryFileIds.add(String(doc.id));
        }
      });
      uploadedTypeRecords.forEach((item) => {
        if (String(item.document_type_value_id) === String(type.id) && item.id != null) {
          statutoryFileIds.add(String(item.id));
          if (item.document_id != null) statutoryFileIds.add(String(item.document_id));
        }
      });
    });

    const isStatutoryFile = (doc: UploadedDoc) => {
      if (statutoryFileIds.has(String(doc.id))) return true;
      if (doc.document_id != null && statutoryFileIds.has(String(doc.document_id))) return true;
      const typeId = getTypeValueId(doc);
      return typeId != null && statutoryTypeIds.has(String(typeId));
    };

    const fromDocs = documents.filter((doc) => {
      if (isStatutoryFile(doc)) return false;
      if (matchesType(doc, otherType)) return true;
      if (String(doc.value_slug || "").toLowerCase().includes("other")) return true;
      const typeId = getTypeValueId(doc);
      if (typeId == otherType.id || String(typeId) === String(otherType.id)) return true;
      return typeId == null;
    });

    const fromTwo = uploadedTypeRecords.filter((item) => {
      if (isStatutoryFile(item)) return false;
      if (matchesType(item, otherType)) return true;
      return (
        item.document_type_value_id == otherType.id ||
        String(item.document_type_value_id) === String(otherType.id)
      );
    });

    const byId = new Map<string, UploadedDoc>();
    fromDocs.forEach((doc) => {
      byId.set(String(doc.id), doc);
    });

    fromTwo.forEach((item) => {
      const existing =
        byId.get(String(item.id)) ||
        (item.document_id != null ? byId.get(String(item.document_id)) : undefined) ||
        fromDocs.find(
          (doc) =>
            doc.document_name &&
            item.document_name &&
            doc.document_name === item.document_name
        );
      const merged = mergeUploadedDocs(item, existing);
      if (!merged) return;
      byId.set(String(existing?.id ?? item.id), merged);
    });

    return Array.from(byId.values()).map((doc) => {
      const fileDoc = documents.find(
        (item) =>
          String(item.id) === String(doc.id) ||
          String(item.id) === String(doc.document_id)
      );
      return mergeUploadedDocs(doc, fileDoc) || doc;
    });
  }, [documents, otherType, statutoryTypes, uploadedTypeRecords]);

  const refreshDocuments = async () => {
    await Promise.all([fetchDocTypes(), fetchDocuments()]);
  };

  const appendBasePayload = (formData: FormData, type: DocType) => {
    formData.append("customerId", customerId);
    formData.append("document_type_id", String(type.document_type_id));
    formData.append("document_type_value_id", String(type.id));
    formData.append("value_slug", type.value_slug);
    formData.append("rental_schedule_id", String(rentalScheduleId));
  };

  const validatePdf = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name} must be less than 10MB`);
      return false;
    }
    if (file.type !== "application/pdf") {
      toast.error(`${file.name} is not a PDF file`);
      return false;
    }
    return true;
  };

  const handleStatutoryUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const type = pendingStatutoryType;
    e.target.value = "";
    if (!file || !type) return;
    if (isOtherType(type)) return;
    if (!validatePdf(file)) return;

    setUploadingTypeId(String(type.id));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", file.name);
      appendBasePayload(formData, type);

      await apiService.post("/rental-schedule/document_upload/other_docs", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      await refreshDocuments();
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingTypeId("");
      setPendingStatutoryType(null);
    }
  };

  const handleOtherMultiUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!customFileName.trim()) {
      toast.error("Please enter a document name");
      return;
    }

    const validFiles = files.filter(validatePdf);
    if (!validFiles.length) return;

    setUploadingOther(true);
    try {
      const formData = new FormData();
      validFiles.forEach((file) => formData.append("files", file));
      formData.append("file_name", customFileName.trim());
      appendBasePayload(formData, otherType);

      await apiService.post(
        "/rental-schedule/document_upload/other_docs/multiple",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success(
        validFiles.length === 1
          ? "Document uploaded successfully"
          : `${validFiles.length} documents uploaded successfully`
      );
      setCustomFileName("");
      await refreshDocuments();
    } catch (error) {
      console.error("Failed to upload documents:", error);
      toast.error("Failed to upload documents");
    } finally {
      setUploadingOther(false);
    }
  };

  const openViewPopup = (url: string, name: string, date: string) => {
    setViewDocOpen(true);
    setPopupName(name);
    setPopupUrl(url);
    setPopupDate(date);
  };

  const closeViewPopup = () => {
    setViewDocOpen(false);
    setPopupName("");
    setPopupUrl("");
    setPopupDate("");
  };

  const updateDocUrl = (documentId: string, url: string) => {
    const patch = <T extends UploadedDoc>(prev: T[]) =>
      prev.map((item) =>
        String(item.id) === documentId || String(item.document_id) === documentId
          ? { ...item, url }
          : item
      );
    setDocuments(patch);
    setUploadedTypeRecords(patch);
  };

  const resolveFreshDocUrl = async (doc: UploadedDoc): Promise<string> => {
    const resolved = resolveFileDoc(doc);
    const fallbackUrl = getDocUrl(resolved);
    const documentId = getManageDocId(resolved);
    if (documentId == null) return fallbackUrl;

    const key = String(documentId);
    const fetchedAt = docUrlFetchedAtRef.current[key] ?? 0;
    if (Date.now() - fetchedAt < MANAGE_INVOICE_DOC_URL_TTL_MS) return fallbackUrl;

    const url = await fetchManageInvoiceDocUrl(documentId, fallbackUrl);
    docUrlFetchedAtRef.current[key] = Date.now();
    if (url && url !== fallbackUrl) updateDocUrl(key, url);
    return url;
  };

  const handlePreview = async (doc: UploadedDoc) => {
    const resolved = resolveFileDoc(doc);
    const name = resolved.document_name || getDocName(resolved);
    const date = resolved.uploadedAt || getDocDate(resolved);
    const documentId = getManageDocId(resolved);
    if (documentId != null) setResolvingDocId(String(documentId));
    try {
      const url = await resolveFreshDocUrl(resolved);
      openViewPopup(url, name, date);
    } catch (error) {
      console.error("Failed to refresh document URL:", error);
      toast.error("Failed to refresh document. Opening the saved file.");
      openViewPopup(getDocUrl(resolved), name, date);
    } finally {
      setResolvingDocId(null);
    }
  };

  const handleDownload = async (url: string, fileName: string) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleDownloadDoc = async (doc: UploadedDoc) => {
    const resolved = resolveFileDoc(doc);
    const name = resolved.document_name || getDocName(resolved);
    const documentId = getManageDocId(resolved);
    if (documentId != null) setResolvingDocId(String(documentId));
    try {
      const url = await resolveFreshDocUrl(resolved);
      await handleDownload(url, `${name}+.pdf`);
    } catch (error) {
      console.error("Failed to refresh document URL:", error);
      toast.error("Failed to refresh document. Opening the saved file.");
      await handleDownload(getDocUrl(resolved), `${name}+.pdf`);
    } finally {
      setResolvingDocId(null);
    }
  };

  const openDeletePopup = (doc: UploadedDoc) => {
    setDeletePopup(true);
    setDeleteDocId(doc.id);
    setPopupName(getDocName(doc));
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteDocId(undefined);
    setPopupName("");
  };

  const deleteDoc = async () => {
    try {
      await apiService.patch("/customers/other_documents/delete", {
        delete_doc_id: deleteDocId,
        admin_user_id: getLocalStorageItem("detail")?.userId,
      });
      toast.success("Document deleted successfully");
      await refreshDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    } finally {
      closeDeletePopup();
    }
  };

  const renderUploadMeta = (doc?: UploadedDoc) => {
    if (!doc) return null;
    const resolved = resolveFileDoc(doc);
    const date = formatDocDate(getDocDate(resolved));
    const uploader = resolved.admin_name || getDocUploader(resolved);
    if (!date && !uploader) return null;

    return (
      <>
        {date && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap shrink-0">
            <Calendar className="h-3 w-3" />
            {date}
          </span>
        )}
        {uploader && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap shrink-0">
            <UserCheck className="h-3 w-3" />
            {uploader}
          </span>
        )}
      </>
    );
  };

  const renderDocActions = (doc: UploadedDoc) => {
    const resolved = resolveFileDoc(doc);
    const documentId = getManageDocId(resolved);
    const isResolving = documentId != null && resolvingDocId === String(documentId);

    return (
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={isResolving}
          onClick={() => void handlePreview(resolved)}
        >
          {isResolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={isResolving}
          onClick={() => void handleDownloadDoc(resolved)}
        >
          {isResolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => openDeletePopup(resolved)}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    );
  };

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between pb-3 border-b">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Executed Documents
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage statutory document requirements.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onViewSignatories}>
          <UserCheck className="h-4 w-4 mr-2" />
          Authorised Signatory
        </Button>
      </div>

      <div className="flex flex-nowrap items-stretch gap-4 w-full">
        <div className="flex-1 min-w-0 border rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-[#3F8B98]/15 shrink-0">
              <FileText className="h-5 w-5 text-[#3F8B98]" />
            </div>
            <h3 className="font-semibold text-sm min-w-0 truncate">Rental Schedule</h3>
            <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={onViewSchedule}>
              <Eye className="h-4 w-4 mr-2" />
              View Schedule
            </Button>
          </div>
        </div>
        <div className="flex-1 min-w-0 border rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-[#3F8B98]/15 shrink-0">
              <FileText className="h-5 w-5 text-[#3F8B98]" />
            </div>
            <h3 className="font-semibold text-sm min-w-0 truncate">Leasing Documentation</h3>
            <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={onViewLeasingDocs}>
              <Eye className="h-4 w-4 mr-2" />
              View Leasing Docs
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Statutory Checklist
        </h3>
        <div className="border rounded-lg divide-y">
          {statutoryTypes.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No statutory document types available.
            </div>
          ) : (
            statutoryTypes.map((type) => {
              const uploaded = getDocForType(type);
              const received = isTypeUploaded(type) || !!uploaded;
              const isUploading = uploadingTypeId === String(type.id);

              return (
                <div key={String(type.id)} className="flex items-center justify-between gap-4 p-4 flex-nowrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`p-2 rounded-md shrink-0 ${received ? "bg-green-50" : "bg-[#3F8B98]/15"}`}>
                      <FileText className={`h-4 w-4 ${received ? "text-green-600" : "text-[#3F8B98]"}`} />
                    </div>
                    <div className="font-medium text-sm truncate min-w-0">{type.value_name}</div>
                    {received && renderUploadMeta(uploaded)}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {received ? (
                      uploaded && renderDocActions(uploaded)
                    ) : (
                      <>
                        <Badge variant="orange">Pending</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isUploading}
                          onClick={() => {
                            setPendingStatutoryType(type);
                            statutoryFileRef.current?.click();
                          }}
                        >
                          {isUploading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Other Documents
          </h3>
          <span className="text-xs text-muted-foreground">
            {otherDocuments.length} attached
          </span>
        </div>
        <div className="border border-dashed rounded-lg bg-muted/10">
          {otherDocuments.length > 0 && (
            <div className="divide-y">
              {otherDocuments.map((doc) => {
                return (
                  <div key={String(doc.id)} className="flex items-center justify-between gap-4 p-4 bg-background rounded-t-lg flex-nowrap">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="p-2 bg-green-50 rounded-md shrink-0">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="font-medium text-sm truncate min-w-0">{getDocName(doc)}</div>
                      {renderUploadMeta(doc)}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {renderDocActions(doc)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="p-4 space-y-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Document Name</Label>
              <Input
                placeholder="Enter document name..."
                value={customFileName}
                onChange={(e) => setCustomFileName(e.target.value)}
                disabled={uploadingOther}
              />
            </div>
            <Button
              variant="link"
              className="text-[#3F8B98] hover:text-[#2f6d78] font-semibold px-2 h-auto"
              disabled={uploadingOther}
              onClick={() => {
                if (!customFileName.trim()) {
                  toast.error("Please enter a document name");
                  return;
                }
                otherFileRef.current?.click();
              }}
            >
              {uploadingOther ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {uploadingOther ? "Uploading..." : "Add other document"}
            </Button>
            <p className="text-xs text-muted-foreground mt-1 px-2">
              Select multiple PDF files at once
            </p>
          </div>
        </div>
      </div>

      <input
        ref={statutoryFileRef}
        type="file"
        className="sr-only"
        accept=".pdf"
        onChange={handleStatutoryUpload}
      />
      <input
        ref={otherFileRef}
        type="file"
        className="sr-only"
        accept=".pdf"
        multiple
        onChange={handleOtherMultiUpload}
      />

      <Sheet open={viewDocOpen} onOpenChange={(open) => !open && closeViewPopup()}>
        <SheetContent
          side="right"
          className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        >
          <SheetHeader className="shrink-0 space-y-1 border-b border-slate-100 px-6 py-4 pr-12 text-left">
            <SheetTitle className="truncate">{popupName}</SheetTitle>
            <SheetDescription>
              {popupDate ? `Uploaded on ${popupDate}` : "Document preview"}
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 p-4 pt-3">
            <div className="h-full overflow-hidden rounded-lg border bg-muted/30">
              {popupUrl ? (
                <iframe src={popupUrl} className="h-full w-full" title={popupName} />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <FileText className="mx-auto mb-3 h-12 w-12 opacity-50" />
                    <p>Preview not available</p>
                    <p className="mt-1 text-xs">Document will be viewable after storage upload</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {deletePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete
              <strong> {popupName} </strong>?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
                onClick={closeDeletePopup}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={deleteDoc}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
